"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, ArrowRight, Zap } from "lucide-react";

function FloatingBall({ style }: { style: React.CSSProperties }) {
  return (
    <div className="absolute rounded-full border-2 border-white/10 pointer-events-none" style={style} />
  );
}

function HexPattern() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hex" x="0" y="0" width="60" height="52" patternUnits="userSpaceOnUse">
          <polygon points="30,2 58,17 58,35 30,50 2,35 2,17" fill="none" stroke="white" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hex)" />
    </svg>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.error ?? "Terjadi gangguan. Silakan coba lagi.");
        setLoading(false);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }

      router.push("/dashboard");
      router.refresh();
    } catch {
      setError("Tidak bisa terhubung ke server. Coba lagi.");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ background: "#1A0B0C" }}>

      {/* ── LEFT HERO PANEL ── */}
      <div className="relative hidden lg:flex flex-col flex-1 overflow-hidden" style={{ background: "linear-gradient(145deg, #1A0B0C 0%, #8F1C21 60%, #1A0B0C 100%)" }}>
        <HexPattern />

        {/* Floating decorative circles */}
        <FloatingBall style={{ width: 420, height: 420, top: -80, left: -80, background: "radial-gradient(circle, rgba(233, 102, 18,0.12) 0%, transparent 70%)", border: "none" }} />
        <FloatingBall style={{ width: 280, height: 280, bottom: -60, right: -60, background: "radial-gradient(circle, rgba(143, 28, 33,0.4) 0%, transparent 70%)", border: "none" }} />
        <FloatingBall style={{ width: 120, height: 120, top: "18%", right: "12%", animation: "float1 6s ease-in-out infinite" }} />
        <FloatingBall style={{ width: 60, height: 60, top: "55%", left: "8%", animation: "float2 8s ease-in-out infinite", borderColor: "rgba(233, 102, 18,0.3)" }} />
        <FloatingBall style={{ width: 40, height: 40, bottom: "25%", right: "20%", animation: "float1 5s ease-in-out infinite 1s", borderColor: "rgba(233, 102, 18,0.25)" }} />
        <FloatingBall style={{ width: 200, height: 200, top: "38%", left: "42%", animation: "float2 9s ease-in-out infinite 2s", opacity: 0.4 }} />

        {/* Diagonal orange accent bar */}
        <div className="absolute top-0 right-0 w-1 h-full" style={{ background: "linear-gradient(to bottom, transparent, #E96612, transparent)" }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-12 justify-between">
          {/* Top: brand badge */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(233, 102, 18,0.15)", border: "1px solid rgba(233, 102, 18,0.3)" }}>
              <Zap size={16} className="text-[#E96612]" />
            </div>
            <div>
              <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.15em]">Powered by</p>
              <p className="text-white/70 text-xs font-bold">Peruri SuperApp</p>
            </div>
          </div>

          {/* Center: crest + text */}
          <div className="flex flex-col items-center text-center gap-6">
            {/* Crest glow ring */}
            <div className="relative">
              <div className="absolute inset-0 rounded-full blur-3xl scale-110" style={{ background: "radial-gradient(circle, rgba(233, 102, 18,0.35) 0%, transparent 70%)" }} />
              <div className="relative w-40 h-40 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.05)", border: "2px solid rgba(255,255,255,0.1)", boxShadow: "0 0 60px rgba(233, 102, 18,0.2), inset 0 0 40px rgba(0,0,0,0.3)" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/imports/image-5.png" alt="Borneo FC" className="w-28 h-28 object-contain drop-shadow-2xl" />
              </div>
            </div>

            <div>
              <p className="text-[#E96612] text-xs font-extrabold uppercase tracking-[0.25em] mb-2">Borneo FC · Samarinda</p>
              <h1 className="text-white font-extrabold leading-none mb-3" style={{ fontSize: "clamp(2.4rem, 4vw, 3.4rem)", textShadow: "0 4px 40px rgba(0,0,0,0.5)" }}>
                SUPER<span style={{ color: "#E96612" }}>APP</span>
                <br />ADMIN
              </h1>
              <p className="text-white/40 text-sm font-medium max-w-xs mx-auto leading-relaxed">
                Pusat kendali operasional tiket, user, dan kanal digital Borneo FC
              </p>
            </div>

            {/* Stat chips */}
            <div className="flex items-center gap-3 flex-wrap justify-center">
              {[
                { n: "12", label: "Laga Aktif" },
                { n: "47K", label: "Fans Terdaftar" },
                { n: "Rp 2.8M", label: "Revenue" },
              ].map((s) => (
                <div key={s.label} className="px-4 py-2 rounded-full text-center" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <p className="text-white font-extrabold text-sm leading-none">{s.n}</p>
                  <p className="text-white/40 text-[10px] font-semibold mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: footer */}
          <p className="text-white/20 text-xs text-center font-medium">
            © 2026 Borneo FC · PT Peruri Digital Security
          </p>
        </div>
      </div>

      {/* ── RIGHT LOGIN PANEL ── */}
      <div className="relative flex flex-col justify-center items-center w-full lg:w-[460px] shrink-0 px-8 py-12" style={{ background: "#F5EDEC" }}>
        {/* Top mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/imports/image-5.png" alt="Borneo FC" className="w-12 h-12 object-contain" />
          <div>
            <p className="font-extrabold text-[#1A0B0C] text-lg leading-tight">Borneo FC</p>
            <p className="text-[#717171] text-[11px] font-bold uppercase tracking-widest">SuperApp Admin</p>
          </div>
        </div>

        {/* Decorative top-right blob */}
        <div className="absolute top-0 right-0 w-48 h-48 rounded-bl-full pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(233, 102, 18,0.08), transparent)" }} />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-tr-full pointer-events-none" style={{ background: "linear-gradient(315deg, rgba(143, 28, 33,0.07), transparent)" }} />

        <div className="relative w-full max-w-sm">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8" style={{ boxShadow: "0 24px 80px rgba(143, 28, 33,0.12), 0 0 0 1px rgba(143, 28, 33,0.06)" }}>

            {/* Header */}
            <div className="mb-8">
              <div className="w-12 h-12 rounded-2xl mb-4 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #8F1C21, #2F44D4)" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/imports/image-5.png" alt="Borneo FC" className="w-8 h-8 object-contain" />
              </div>
              <h2 className="text-2xl font-extrabold text-[#1A0B0C] leading-tight">Selamat datang<span style={{ color: "#E96612" }}>.</span></h2>
              <p className="text-[#717171] text-sm font-medium mt-1">Masuk ke panel admin Borneo FC SuperApp</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className={shake ? "animate-shake" : ""}>
              <div className="space-y-4 mb-6">
                {/* Email */}
                <div>
                  <label className="block text-xs font-extrabold text-[#1A0B0C] uppercase tracking-[0.08em] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@borneofc.id"
                    className="w-full px-4 py-3 rounded-xl text-sm font-medium text-[#1A0B0C] placeholder-[#C0C5E0] outline-none transition-all"
                    style={{ background: "#F5EDEC", border: "1.5px solid #E9DDDC" }}
                    onFocus={(e) => e.currentTarget.style.borderColor = "#8F1C21"}
                    onBlur={(e) => e.currentTarget.style.borderColor = "#E9DDDC"}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-extrabold text-[#1A0B0C] uppercase tracking-[0.08em] mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-sm font-medium text-[#1A0B0C] placeholder-[#C0C5E0] outline-none transition-all"
                      style={{ background: "#F5EDEC", border: "1.5px solid #E9DDDC" }}
                      onFocus={(e) => e.currentTarget.style.borderColor = "#8F1C21"}
                      onBlur={(e) => e.currentTarget.style.borderColor = "#E9DDDC"}
                    />
                    <button
                      type="button"
                      onClick={() => setShow((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B0B5D8] hover:text-[#8F1C21] transition-colors p-1"
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-xl text-sm font-semibold text-[#DC2626]" style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }}>
                  {error}
                </div>
              )}

              {/* Forgot password */}
              <div className="flex justify-end mb-6">
                <button type="button" className="text-xs font-semibold text-[#717171] hover:text-[#8F1C21] transition-colors">
                  Lupa password?
                </button>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-extrabold text-white text-sm transition-all duration-200 relative overflow-hidden"
                style={{
                  background: loading ? "#C0C5E0" : "linear-gradient(135deg, #E96612 0%, #E07800 100%)",
                  boxShadow: loading ? "none" : "0 8px 24px rgba(233, 102, 18,0.35)",
                  transform: loading ? "none" : undefined,
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(233, 102, 18,0.45)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = loading ? "none" : "0 8px 24px rgba(233, 102, 18,0.35)"; }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Memverifikasi...
                  </>
                ) : (
                  <>
                    Masuk ke Dashboard
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Hint */}
          <p className="text-center text-[#B0B5D8] text-xs font-medium mt-6">
            Hanya untuk staff & admin resmi Borneo FC
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-18px) rotate(8deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(14px) rotate(-6deg); }
        }
        @keyframes animate-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .animate-shake { animation: animate-shake 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}
